import { useState, type ElementType, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

type Tone = "default" | "red" | "amber" | "green" | "cyan";

interface MetaTileProps {
  label: string;
  value: string | number;
  tone?: Tone;
}

interface RouteHeroProps {
  eyebrow: string;
  icon?: ElementType;
  numeral: string | number;
  numeralTone?: Tone;
  numeralSuffix?: string;
  title: string;
  summary?: string;
  collapsibleSummary?: boolean;
  meta?: MetaTileProps[];
  actions?: ReactNode;
}

const NUMERAL_TONE: Record<Tone, string> = {
  default: "text-[oklch(0.93_0.008_240)]",
  red: "text-[oklch(0.78_0.18_25)]",
  amber: "text-[oklch(0.85_0.14_80)]",
  green: "text-[oklch(0.80_0.15_155)]",
  cyan: "text-[oklch(0.85_0.13_190)]",
};

const TILE_VALUE_TONE: Record<Tone, string> = {
  default: "text-[oklch(0.97_0.005_240)]",
  red: "text-[oklch(0.82_0.18_25)]",
  amber: "text-[oklch(0.88_0.15_80)]",
  green: "text-[oklch(0.83_0.16_155)]",
  cyan: "text-[oklch(0.88_0.14_190)]",
};

// Tone-tinted tile surface so each KPI reads as its own coloured chip.
const TILE_TINT: Record<Tone, string> = {
  default: "border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.05)]",
  red: "border-[oklch(0.78_0.18_25_/_0.40)] bg-[oklch(0.78_0.18_25_/_0.10)]",
  amber: "border-[oklch(0.85_0.14_80_/_0.40)] bg-[oklch(0.85_0.14_80_/_0.10)]",
  green: "border-[oklch(0.80_0.15_155_/_0.40)] bg-[oklch(0.80_0.15_155_/_0.10)]",
  cyan: "border-[oklch(0.85_0.13_190_/_0.40)] bg-[oklch(0.85_0.13_190_/_0.10)]",
};

const TILE_DOT: Record<Tone, string> = {
  default: "bg-[oklch(0.85_0.01_240_/_0.55)]",
  red: "bg-[oklch(0.82_0.18_25)]",
  amber: "bg-[oklch(0.88_0.15_80)]",
  green: "bg-[oklch(0.83_0.16_155)]",
  cyan: "bg-[oklch(0.88_0.14_190)]",
};

export function RouteHero({
  eyebrow,
  icon: Icon,
  numeral,
  numeralTone = "default",
  numeralSuffix,
  title,
  summary,
  collapsibleSummary = false,
  meta,
  actions,
}: RouteHeroProps) {
  const [summaryOpen, setSummaryOpen] = useState(!collapsibleSummary);
  return (
    <div className="hero-panel relative overflow-hidden rounded-2xl">
      <div className="scanlines pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative grid gap-8 p-7 sm:p-9 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-10 lg:p-10">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-[oklch(0.72_0.17_190_/_0.30)] bg-[oklch(0.72_0.17_190_/_0.10)] px-3 py-1 text-[11px] font-medium text-[oklch(0.85_0.15_190)]">
              {Icon ? <Icon className="size-3" /> : null}
              <span className="font-data tracking-[0.12em] uppercase">{eyebrow}</span>
            </span>
          </div>

          <div className="flex items-end gap-5">
            <span
              className={`font-data text-[140px] leading-[0.85] font-medium tracking-[-0.04em] sm:text-[170px] ${NUMERAL_TONE[numeralTone]}`}
            >
              {numeral}
            </span>
            {numeralSuffix ? (
              <span className="font-data pb-5 text-[20px] leading-none font-medium text-[oklch(0.85_0.01_240_/_0.55)]">
                {numeralSuffix}
              </span>
            ) : null}
          </div>

          <div className="flex max-w-2xl flex-col gap-2">
            <h1 className="text-[26px] leading-[1.15] font-bold tracking-tight text-[oklch(0.97_0.005_240)] sm:text-[32px]">
              {title}.
            </h1>
            {summary ? (
              collapsibleSummary ? (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setSummaryOpen((v) => !v)}
                    aria-expanded={summaryOpen}
                    className="font-data inline-flex w-fit items-center gap-1.5 text-[10px] tracking-[0.18em] text-[oklch(0.85_0.01_240_/_0.55)] uppercase transition-colors hover:text-[oklch(0.85_0.15_190)]"
                  >
                    {summaryOpen ? "Hide summary" : "Show summary"}
                    <ChevronDown
                      className={`size-3 transition-transform ${summaryOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {summaryOpen ? (
                    <p className="text-[14px] leading-relaxed text-[oklch(0.85_0.01_240_/_0.70)]">
                      {summary}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-[14px] leading-relaxed text-[oklch(0.85_0.01_240_/_0.70)]">
                  {summary}
                </p>
              )
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {meta && meta.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {meta.map((m) => {
                const tone = m.tone ?? "default";
                return (
                  <div
                    key={m.label}
                    className={`flex flex-col gap-2 rounded-xl border px-4 py-3.5 ${TILE_TINT[tone]}`}
                  >
                    <span className="font-data flex items-center gap-1.5 text-[10px] tracking-[0.16em] text-[oklch(0.90_0.01_240_/_0.75)] uppercase">
                      <span className={`size-1.5 rounded-full ${TILE_DOT[tone]}`} aria-hidden />
                      {m.label}
                    </span>
                    <span
                      className={`font-data text-[34px] leading-none font-semibold tabular-nums ${TILE_VALUE_TONE[tone]}`}
                    >
                      {m.value}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
          {actions ? <div className="flex flex-col gap-2">{actions}</div> : null}
        </div>
      </div>
    </div>
  );
}
